import api from './apiClient';

// Azure Speech SDK büyük bir paket; ana bundle'ı şişirmemek için
// ilk kullanımda dinamik import ile yüklenir.
let sdkPromise = null;
const loadSdk = () => {
  if (!sdkPromise) sdkPromise = import('microsoft-cognitiveservices-speech-sdk');
  return sdkPromise;
};

// ─── Token yönetimi ───────────────────────────────────────────────────────────
// Backend'den alınan token 10 dk geçerli; 8 dk cache'lenir.
let cachedToken = null;
let tokenExpiresAt = 0;

const getSpeechToken = async () => {
  if (cachedToken && Date.now() < tokenExpiresAt) return cachedToken;
  const response = await api.get('/Speech/token');
  cachedToken = response.data;
  tokenExpiresAt = Date.now() + 8 * 60 * 1000;
  return cachedToken;
};

const createSpeechConfig = async () => {
  const SpeechSDK = await loadSdk();
  const { token, region } = await getSpeechToken();
  const config = SpeechSDK.SpeechConfig.fromAuthorizationToken(token, region);
  config.speechRecognitionLanguage = 'tr-TR';
  config.speechSynthesisVoiceName = 'tr-TR-EmelNeural';
  return config;
};

// ─── Konuşma tanıma (STT) ─────────────────────────────────────────────────────
let activeRecognizer = null;

// Tek soru tanır: kullanıcı susunca otomatik durur.
// Dönüş: tanınan metin; ses yakalanamadıysa boş string.
export const recognizeOnce = async () => {
  const SpeechSDK = await loadSdk();
  const speechConfig = await createSpeechConfig();
  const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
  const recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);
  activeRecognizer = recognizer;

  return new Promise((resolve, reject) => {
    recognizer.recognizeOnceAsync(
      (result) => {
        recognizer.close();
        if (activeRecognizer === recognizer) activeRecognizer = null;
        if (result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          resolve(result.text || '');
        } else {
          // NoMatch (sessizlik) veya iptal: hata değil, boş sonuç
          resolve('');
        }
      },
      (err) => {
        recognizer.close();
        if (activeRecognizer === recognizer) activeRecognizer = null;
        reject(new Error(err || 'Ses tanıma başarısız oldu.'));
      }
    );
  });
};

// Devam eden tanımayı iptal eder (recognizeOnce boş string ile döner)
export const stopListening = () => {
  if (activeRecognizer) {
    try { activeRecognizer.close(); } catch { /* zaten kapalı */ }
    activeRecognizer = null;
  }
};

// ─── Seslendirme (TTS) ────────────────────────────────────────────────────────
// Mobil tarayıcılar programatik sesi ancak kullanıcı jestiyle hazırlanmış bir
// kanaldan çalar. İki katman kullanılır:
//   1) HTMLAudio: jest içinde sessiz WAV ile "kilidi açılan" tek element
//   2) Web Audio API: jest içinde 'running' duruma getirilen AudioContext —
//      element politikaya takılırsa decode edip buradan çalınır
let audioEl = null;
let audioCtx = null;
let activeSource = null; // WebAudio buffer source (durdurma için)
let activeUrl = null;
let activeResolve = null;

// 1 örneklik sessiz WAV — elementin kilidini jest içinde açmak için
const SILENT_WAV = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAA==';

// Mikrofon tıklamasının (kullanıcı jesti) içinde, herhangi bir await'ten ÖNCE çağrılmalı
export const primeAudioPlayback = () => {
  if (!audioEl) {
    // Element her durumda saklanır: play reddedilse bile yeni elementten kötü olamaz
    audioEl = new Audio(SILENT_WAV);
    audioEl.play().catch(() => { /* kilit bir sonraki jestte tekrar denenir */ });
  }
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (Ctx && !audioCtx) audioCtx = new Ctx();
  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => { /* jest dışında resume edilemez; sonraki jest dener */ });
  }
};

const finishPlayback = () => {
  if (activeUrl) { URL.revokeObjectURL(activeUrl); activeUrl = null; }
  activeSource = null;
  if (activeResolve) { activeResolve(); activeResolve = null; }
};

// Metni seslendirir; promise oynatma bittiğinde (veya durdurulduğunda) çözülür.
export const speakText = async (text) => {
  if (!text?.trim()) return;
  stopSpeaking();

  const SpeechSDK = await loadSdk();
  const speechConfig = await createSpeechConfig();
  speechConfig.speechSynthesisOutputFormat =
    SpeechSDK.SpeechSynthesisOutputFormat.Audio16Khz32KBitRateMonoMp3;

  // audioConfig null: SDK hoparlöre çalmaz, ses verisini ArrayBuffer döner
  const synthesizer = new SpeechSDK.SpeechSynthesizer(speechConfig, null);
  const audioData = await new Promise((resolve, reject) => {
    synthesizer.speakTextAsync(
      text,
      (result) => {
        synthesizer.close();
        if (result.reason === SpeechSDK.ResultReason.SynthesizingAudioCompleted) {
          resolve(result.audioData);
        } else {
          reject(new Error('Seslendirme üretilemedi.'));
        }
      },
      (err) => {
        synthesizer.close();
        reject(new Error(err || 'Seslendirme başarısız oldu.'));
      }
    );
  });

  return new Promise((resolve, reject) => {
    activeResolve = resolve;

    // Katman 2: element politikaya takılırsa WebAudio ile çal
    const fallbackToWebAudio = (elementErr) => {
      if (!audioCtx) {
        activeResolve = null;
        reject(new Error(`Ses çalınamadı (${elementErr?.name || elementErr}).`));
        return;
      }
      Promise.resolve()
        .then(() => (audioCtx.state === 'suspended' ? audioCtx.resume() : null))
        .then(() => audioCtx.decodeAudioData(audioData.slice(0)))
        .then((buffer) => {
          const source = audioCtx.createBufferSource();
          source.buffer = buffer;
          source.connect(audioCtx.destination);
          source.onended = finishPlayback;
          activeSource = source;
          source.start();
        })
        .catch((ctxErr) => {
          activeResolve = null;
          reject(new Error(
            `Ses çalınamadı (element: ${elementErr?.name || elementErr}, webaudio: ${ctxErr?.name || ctxErr}).`
          ));
        });
    };

    // Katman 1: kilidi açılmış HTMLAudio elementi
    if (!audioEl) audioEl = new Audio(); // masaüstü fallback (jest kısıtı yok)
    activeUrl = URL.createObjectURL(new Blob([audioData], { type: 'audio/mpeg' }));
    audioEl.onended = finishPlayback;
    audioEl.onerror = finishPlayback;
    audioEl.src = activeUrl;
    audioEl.play().catch(fallbackToWebAudio);
  });
};

// Oynatmayı durdurur; bekleyen speakText promise'ı çözülür.
export const stopSpeaking = () => {
  if (audioEl) {
    try { audioEl.pause(); audioEl.removeAttribute('src'); } catch { /* element boş */ }
  }
  if (activeSource) {
    try { activeSource.stop(); } catch { /* zaten durmuş */ }
  }
  finishPlayback();
};
